<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800">Reporte por Tipo de Contacto</h1>
      <div class="flex space-x-2 items-center">
        <input type="date" v-model="fechas.fecha_inicio" class="px-4 py-2 border rounded">
        <input type="date" v-model="fechas.fecha_fin" class="px-4 py-2 border rounded">
        <button @click="cargarDatos" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          Consultar
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-10">
      <p>Cargando reporte...</p>
    </div>
    <div v-if="error" class="text-center py-10 text-red-500">
      <p>{{ error }}</p>
    </div>

    <div v-if="!loading && !error && datos" class="bg-white rounded-lg shadow p-6 mb-6">
      <div class="mb-6">
        <h2 class="text-xl font-semibold mb-2">Resumen de Contactos</h2>
        <p class="text-gray-600">
          Análisis de los contactos clasificados según su tipo para el período seleccionado.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="border rounded-lg p-4">
          <p class="text-sm text-gray-500 mb-1">Total de Contactos</p>
          <p class="text-2xl font-bold">{{ datos.resumen.total_contactos }}</p>
        </div>
        <div class="border rounded-lg p-4">
          <p class="text-sm text-gray-500 mb-1">Tipo Más Común</p>
          <p class="text-xl font-bold truncate">{{ datos.resumen.tipo_mas_comun?.tipo_contacto || 'N/A' }}</p>
          <p class="text-xs text-gray-500 mt-1">{{ datos.resumen.tipo_mas_comun?.cantidad || 0 }} contactos</p>
        </div>
        <div class="border rounded-lg p-4">
          <p class="text-sm text-gray-500 mb-1">Tasa de Conversión</p>
          <p class="text-2xl font-bold">{{ datos.resumen.tasa_conversion.toFixed(1) }}%</p>
          <p class="text-xs text-gray-500 mt-1">De contacto a venta</p>
        </div>
        <div class="border rounded-lg p-4">
          <p class="text-sm text-gray-500 mb-1">Mejor Día</p>
          <p class="text-xl font-bold">{{ datos.resumen.mejor_dia?.dia || 'N/A' }}</p>
          <p class="text-xs text-gray-500 mt-1">{{ datos.resumen.mejor_dia?.cantidad || 0 }} contactos</p>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div class="lg:col-span-2">
          <div class="border rounded-lg p-4 flex items-center justify-center">
            <h3 class="font-semibold mb-4 absolute">Distribución por Tipo de Contacto</h3>
            <div class="h-80 w-full">
              <apexchart v-if="seriesTipos.length > 0" type="doughnut" :options="chartOptionsTipos" :series="seriesTipos"></apexchart>
            </div>
          </div>
        </div>
        
        <div>
          <div class="border rounded-lg p-4 h-full">
            <h3 class="font-semibold mb-4">Efectividad por Tipo</h3>
            <div v-if="datos.distribucion.length > 0" class="space-y-4">
              <div v-for="tipo in datos.distribucion" :key="tipo.tipo_contacto" class="border-b pb-3 last:border-b-0">
                <div class="flex justify-between items-center mb-1">
                  <span class="font-medium">{{ tipo.tipo_contacto }}</span>
                  <span class="text-sm font-semibold">{{ tipo.tasa_conversion.toFixed(1) }}%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="h-2 rounded-full" :style="`width: ${tipo.tasa_conversion}%; background-color: ${getColor(tipo.tipo_contacto)};`"></div>
                </div>
                <div class="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{{ tipo.cantidad }} contactos</span>
                  <span>{{ tipo.convertidos }} conversiones</span>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-gray-500 py-4">No hay datos de distribución.</div>
          </div>
        </div>
      </div>

      <div class="border rounded-lg p-4 mb-8">
        <h3 class="font-semibold mb-4">Tendencia de Contactos</h3>
        <div class="h-80">
          <apexchart v-if="seriesTendencia[0] && seriesTendencia[0].data.length > 0" type="line" :options="chartOptionsTendencia" :series="seriesTendencia"></apexchart>
        </div>
      </div>

      <div>
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold">Registros de Contacto</h3>
          <div class="flex items-center space-x-2">
            <input type="text" v-model="busqueda" placeholder="Buscar..." class="px-3 py-1 border rounded text-sm">
            <select v-model="filtroTipo" class="px-3 py-1 border rounded text-sm">
              <option value="">Todos los tipos</option>
              <option v-for="tipo in tiposUnicos" :key="tipo" :value="tipo">{{ tipo }}</option>
            </select>
          </div>
        </div>
        
        <div v-if="contactosFiltrados.length > 0" class="overflow-x-auto">
          <table class="min-w-full bg-white">
            <thead>
              <tr class="bg-gray-100">
                <th class="py-3 px-4 text-left">Fecha</th>
                <th class="py-3 px-4 text-left">Nombre</th>
                <th class="py-3 px-4 text-left">Tipo</th>
                <th class="py-3 px-4 text-left">Interés</th>
                <th class="py-3 px-4 text-left">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="contacto in paginatedContactos" :key="contacto.id" class="border-t hover:bg-gray-50">
                <td class="py-3 px-4">{{ formatDate(contacto.fecha_contacto) }}</td>
                <td class="py-3 px-4 font-medium">{{ contacto.nombre_completo }}</td>
                <td class="py-3 px-4">
                  <span class="px-2 py-1 text-xs rounded-full" :style="`background-color: ${getColor(contacto.tipo_contacto)}20; color: ${getColor(contacto.tipo_contacto)};`">
                    {{ contacto.tipo_contacto }}
                  </span>
                </td>
                <td class="py-3 px-4"><span class="text-sm">{{ contacto.interes }}</span></td>
                <td class="py-3 px-4">
                  <span class="px-2 py-1 text-xs rounded-full" :class="getStatusClass(contacto.status)">{{ contacto.status }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center text-gray-500 py-8">No se encontraron contactos con los filtros aplicados.</div>
        
        <div class="flex justify-between items-center mt-4">
          <div class="text-sm text-gray-600">
            Mostrando {{ paginatedContactos.length }} de {{ contactosFiltrados.length }} contactos
          </div>
          <div v-if="contactosFiltrados.length > pageSize">
             <button @click="currentPage--" :disabled="currentPage === 1" class="px-3 py-1 border rounded">Anterior</button>
             <span class="px-3">Página {{ currentPage }} de {{ totalPages }}</span>
             <button @click="currentPage++" :disabled="currentPage === totalPages" class="px-3 py-1 border rounded">Siguiente</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import reportesNuevos from '@/Services/reportesNuevos.js';
import VueApexCharts from 'vue3-apexcharts';

export default {
  name: 'ReporteTiposContacto',
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);

    return {
      fechas: {
        fecha_inicio: startDate.toISOString().split('T')[0],
        fecha_fin: endDate.toISOString().split('T')[0],
      },
      datos: null,
      loading: false,
      error: null,
      filtroTipo: '',
      busqueda: '',
      currentPage: 1,
      pageSize: 10,
      colores: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6'],
      seriesTipos: [],
      chartOptionsTipos: {
        chart: { type: 'doughnut' },
        labels: [],
        colors: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6'],
        legend: { position: 'right' }
      },
      seriesTendencia: [],
      chartOptionsTendencia: {
        chart: { type: 'line', zoom: { enabled: false } },
        stroke: { curve: 'smooth' },
        xaxis: { categories: [] },
        yaxis: { min: 0, tickAmount: 5, labels: { formatter: (val) => { return val.toFixed(0); } } }
      }
    };
  },
  computed: {
    contactosFiltrados() {
      if (!this.datos || !this.datos.contactos) return [];
      return this.datos.contactos.filter(contacto => {
        const cumpleTipo = !this.filtroTipo || contacto.tipo_contacto === this.filtroTipo;
        const cumpleBusqueda = !this.busqueda || 
          Object.values(contacto).some(val => 
            String(val).toLowerCase().includes(this.busqueda.toLowerCase())
          );
        return cumpleTipo && cumpleBusqueda;
      });
    },
    paginatedContactos() {
        const start = (this.currentPage - 1) * this.pageSize;
        const end = start + this.pageSize;
        return this.contactosFiltrados.slice(start, end);
    },
    totalPages() {
        return Math.ceil(this.contactosFiltrados.length / this.pageSize);
    },
    tiposUnicos() {
      if (!this.datos || !this.datos.distribucion) return [];
      return this.datos.distribucion.map(d => d.tipo_contacto).sort();
    },
  },
  watch: {
    busqueda() {
        this.currentPage = 1;
    },
    filtroTipo() {
        this.currentPage = 1;
    }
  },
  methods: {
    async cargarDatos() {
      this.loading = true;
      this.error = null;
      this.datos = null;
      try {
        const response = await reportesNuevos.REPORTE_TIPO_CONTACTO(this.fechas);
        if (response.data.errors) {
          this.error = Object.values(response.data.errors).join(', ');
          return;
        }
        this.datos = response.data;
        this.$nextTick(() => {
          this.actualizarGraficas();
        });
      } catch (err) {
        console.error('Error al cargar reporte:', err);
        this.error = 'No se pudo cargar el reporte. Intente de nuevo.';
      } finally {
        this.loading = false;
      }
    },
    getColor(tipo) {
      if (!this.datos || !this.datos.distribucion) return '#9CA3AF';
      const index = this.datos.distribucion.findIndex(t => t.tipo_contacto === tipo);
      return index !== -1 ? this.colores[index % this.colores.length] : '#9CA3AF';
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('es-MX', options);
    },
    getStatusClass(status) {
        const classes = {
            'Convertido': 'bg-green-100 text-green-800',
            'En proceso': 'bg-blue-100 text-blue-800',
            'Nuevo': 'bg-yellow-100 text-yellow-800',
            'Perdido': 'bg-red-100 text-red-800'
        };
        return classes[status] || 'bg-gray-100 text-gray-800';
    },
    actualizarGraficas() {
      if (!this.datos) return;

      // Gráfica de tipos de contacto
      if (this.datos.distribucion) {
        this.seriesTipos = this.datos.distribucion.map(t => t.cantidad);
        this.chartOptionsTipos = {
          ...this.chartOptionsTipos,
          labels: this.datos.distribucion.map(t => t.tipo_contacto),
          colors: this.datos.distribucion.map((t, i) => this.colores[i % this.colores.length])
        };
      }
      
      // Gráfica de tendencia
      if (this.datos.tendencia) {
        this.seriesTendencia = [{
          name: 'Número de Contactos',
          data: this.datos.tendencia.map(t => t.cantidad)
        }];
        this.chartOptionsTendencia = {
          ...this.chartOptionsTendencia,
          xaxis: {
            categories: this.datos.tendencia.map(t => t.fecha)
          }
        };
      }
    },
  },
  mounted() {
    this.cargarDatos();
  }
};
</script>

<style scoped>
/* Estilos específicos para este componente */
</style>
